// @ts-strict-ignore
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { ProjectHeadVO } from '@project/model/vo/project.vo';
import { ProjectGroupUtils } from '@project/components/project/util/project-group-utils';
import { StringUtils } from '@shared/util/string-utils';
import { ProjectGroupLoadingService } from '@project/components/project/service/project-group-loading.service';
import { UserModel } from '@user/model/user.model';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ArrayUtils } from '@shared/util/array-utils';
import { LoadProjectService } from '../../../service/load-project.service';
import { Log } from '@shared/debug/log';
import { ProjectNavigationModel, ProjectNavigationTab } from '@project-navigation/project-navigation.model';
import { ProjectNavigationService } from '@project-navigation/service/project-navigation.service';
import { VisibleFeatures } from '@project-navigation/project-navigation.component';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';

@Component({
  standalone: false,
  selector: 'app-construction-project-load',
  templateUrl: './construction-project-load.component.html',
  host: {class: "d-flex flex-column px-2 flex-grow-1"},
})
export class ConstructionProjectLoadComponent implements OnInit {
  public static readonly CONSTRUCTION_VIEW_PROJECTS_LIMIT: number = 500;

  @Output() public finish = new EventEmitter<void>();

  openedFromDashboard: boolean;

  public visibleFeatures: VisibleFeatures = {
    compositeCheckBox: false,
    pinnedProjects: false,
    recentlyUsedProjects: false,
    history: false,
    projectActionsMenu: false,
    projectGroupContextMenu: false,
    newConfigurationGroupBtn: false,
    newFolderBtn: false,
    dialogSearch: true,
    tabs: false,
    breadcrumbs: true,
  }

  public currentSelectedItem: ProjectGroupVO | ProjectHeadVO;
  public expandedProjects: number[] = [];

  public submitEnabled: boolean = false;

  public model: ProjectNavigationModel;

  public showHiddenItems: boolean = false;
  public multipleSelectionWithinSingleProject: boolean = false;
  public isNewProjectCreation: boolean = false;

  private specialName = `myAssignedProjects.groupName.label`;

  constructor(
    private bsModalRef: BsModalRef,
    private userModel: UserModel,
    private projectGroupLoadingService: ProjectGroupLoadingService,
    private loadProjectService: LoadProjectService,
    private projectNavigationService: ProjectNavigationService,
    private localeResourceManager: LocaleResourceManager,
  ) {}

  public ngOnInit() {
    this.openedFromDashboard = window.location.pathname.startsWith("/configurator/open-project");
    this.model = new ProjectNavigationModel();
    this.model.currentTab = ProjectNavigationTab.Search;
    this.model.searchMode = true;
  }

  public async openFolder(projectGroup: ProjectGroupVO): Promise<void> {
    await this.projectNavigationService.openProjectGroup(this.model, projectGroup);
    this.updateViewItems();
  }

  public async openProject(project: ProjectGroupVO): Promise<void> {
    await this.projectNavigationService.openProjectGroup(this.model, project);
    this.model.setCurrentViewedProjectGroup(project);
    this.updateViewItems();
  }

  public updateSubmitEnabled() {
    this.submitEnabled = this.model.currentSelectedItem
      && this.model.currentSelectedItem instanceof ProjectHeadVO;
  }

  public close(): void {
    this.bsModalRef.hide();
    this.finish.emit();
  }

  public async loadProject(): Promise<void> {
    const projectToLoad: ProjectHeadVO = (this.model.currentSelectedItem && this.model.currentSelectedItem instanceof ProjectHeadVO) ?
      this.model.currentSelectedItem : null;
    if (!projectToLoad) {
      return;
    }
    await this.loadProjectService.loadProject(projectToLoad, false).then(() => this.close());
  }

  public async performSearch(searchText: string): Promise<void> {
    this.selectItem(null);
    const actualValue: string = searchText.trim();
    if (StringUtils.isEmpty(actualValue)) {
      this.expandedProjects = [];
      this.model.viewItems = [];
      return;
    }

    try {
      const projectGroups = await this.projectGroupLoadingService.searchProjectGroups(
        actualValue,
        null,
        this.model.toggleExtendedSearch,
        ConstructionProjectLoadComponent.CONSTRUCTION_VIEW_PROJECTS_LIMIT
      );
      this.searchSuccessHandler(projectGroups);
    } catch (error) {
      Log.error(error);
      this.searchFailureHandler();
    }
  }

  private searchSuccessHandler(projectGroups: ProjectGroupVO[]) {
    const groups: ProjectGroupVO[] = projectGroups.filter((g) => ProjectGroupUtils.isConfigurationGroup(g));
    groups.sort((a, b) => ProjectGroupUtils.projectGroupSortFunction(a, b, this.userModel.user.id));
    for (const group of groups) {
      if (ArrayUtils.isNotEmpty(group.projects)) {
        group.projects.sort(ProjectUtils.projectSortByNameFunction)
      }
    }
    this.expandedProjects = groups.map((g) => g.id);
    this.model.viewItems = groups;
    this.updateViewItems();
  };

  private searchFailureHandler() {
    this.expandedProjects = [];
    this.model.viewItems = [];
  };

  public selectItem(item: ProjectGroupVO | ProjectHeadVO): void {
    this.currentSelectedItem = item;
    this.submitEnabled =
      this.currentSelectedItem &&
      (this.currentSelectedItem instanceof ProjectHeadVO ||
        (this.currentSelectedItem instanceof ProjectGroupVO &&
          ProjectGroupUtils.isConfigurationGroupType(this.currentSelectedItem.groupType) &&
          ArrayUtils.isNotEmpty(this.currentSelectedItem.projects)));
  }

  public navigateToTopLevel(): void {
    this.model.clearCurrentViewedProjectGroup();
    this.updateViewItems();
  }

  public updateViewItems(): void {
    const viewItems: (ProjectGroupVO | ProjectHeadVO)[] = [];
    this.model.viewItems.forEach((item) => {
      if (item instanceof ProjectGroupVO) {
        viewItems.push(item);
        if (item.groupType === ProjectGroupUtils.PROJECT && this.expandedProjects.indexOf(item.id) !== -1) {
          viewItems.push(...item.projects);
        }
      }
    });
    this.model.viewItems = [...viewItems];
    this.updateSubmitEnabled();
  }

  public getBreadcrumbLabel(projectGroup: ProjectGroupVO, index: number) {
    if (projectGroup.groupName === this.specialName) {
      return projectGroup.groupOwner.userId === this.userModel.user.id
        ? this.localeResourceManager.getValueByKey('owner.myAssignedProjects.groupName.label')
        : this.localeResourceManager.getValueByKey('myAssignedProjects.groupName.label', {'0': projectGroup.groupOwner.username});
    } else if (index === 0) {
      return this.localeResourceManager.getValueByKey(projectGroup.groupName);
    } else {
      return projectGroup.groupName;
    }
  }
}
